import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

export type ChatTab = 'group' | 'private' | 'broadcast'

interface ChatTabsProps {
  activeTab: ChatTab
  onTabChange: (tab: ChatTab) => void
  /**
   * Whether private chat exists as a surface at all. Note this is NOT the host
   * gate: a closed gate stops you sending, and a moderator can still DM you, so
   * the tab stays and the composer inside it carries the refusal.
   */
  showPrivateTab?: boolean
  /**
   * Broadcast is a host/webinar-only surface. Hidden by default in regular
   * meetings to keep the chat focused on Group + Private; flipped on by
   * MeetingScreen for webinar mode or when the host explicitly enables it.
   */
  showBroadcastTab?: boolean
  /** Unread messages in the group thread. */
  unreadGroup?: number
  /** Unread messages across every private thread, summed. */
  unreadPrivate?: number
}

export function ChatTabs({
  activeTab,
  onTabChange,
  showPrivateTab = true,
  showBroadcastTab = false,
  unreadGroup = 0,
  unreadPrivate = 0,
}: ChatTabsProps) {
  const t = useTranslation()

  const tabClass = (tab: ChatTab) =>
    [
      'flex-1 inline-flex items-center justify-center gap-1.5 rounded-full cursor-pointer px-3 py-2 text-sm font-semibold transition-all',
      activeTab === tab
        ? 'bg-primary text-white shadow-sm'
        : 'text-vc-panel-text-muted hover:text-vc-panel-text',
    ].join(' ')

  // Nothing at zero: a "0" next to a read tab is noise, not information.
  const badge = (tab: ChatTab, count: number) =>
    count > 0 && (
      <span
        className={[
          'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] leading-none',
          activeTab === tab ? 'bg-white text-primary' : 'bg-primary text-white',
        ].join(' ')}
      >
        {count > 99 ? '99+' : count}
      </span>
    )

  return (
    <div className="mobile-sheet-header flex-shrink-0 border-b border-vc-panel-border px-4 py-3 bg-panelcolor">
      <div className="flex gap-1 rounded-full bg-vc-panel-input-bg p-1">
        <button type="button" onClick={() => onTabChange('group')} className={tabClass('group')}>
          {t('panels.group')}
          {badge('group', unreadGroup)}
        </button>
        {showPrivateTab && (
          <button type="button" onClick={() => onTabChange('private')} className={tabClass('private')}>
            {t('panels.private')}
            {badge('private', unreadPrivate)}
          </button>
        )}
        {showBroadcastTab && (
          <button type="button" onClick={() => onTabChange('broadcast')} className={tabClass('broadcast')}>
            {t('panels.broadcast')}
          </button>
        )}
      </div>
    </div>
  )
}
